// ToolEvidence — the collapsed card every tool call leaves behind.
//
// Before this, a turn that ran five MCP calls looked identical to one
// that answered from memory: the pane jumped from "Thinking" to a reply
// with nothing in between. The events were already on the wire
// (`tool_started` / `tool_completed`); the SPA simply dropped them.
//
// Collapsed by default and deliberately quiet. This is evidence, not
// content — it should be findable when you go looking and invisible when
// you aren't. Expanding shows the arguments and the result, which is the
// difference between "it called secret_get" and "it called secret_get on
// *that* path".
//
// A tool that drew its own UI renders that below the card rather than
// instead of it: the UI is what the tool wanted to say, the card is what
// it actually did.

import React from 'react';
import { InlineAccordion } from 'common';
import { BsCheck2, BsExclamationTriangleFill, BsTools } from 'react-icons/bs';

export interface ToolEvidenceProps {
    name: string;
    input?: unknown;
    output?: string;
    isError?: boolean;
    running?: boolean;
}

/** Bounded so a build log doesn't turn the card into the page. */
const MAX_OUTPUT_CHARS = 4000;

function renderInput(input: unknown): string {
    if (input === undefined || input === null) return '';
    if (typeof input === 'string') return input;
    try {
        return JSON.stringify(input, null, 2);
    } catch {
        return String(input);
    }
}

// A one-line gloss of the arguments for the collapsed header — enough to
// tell two calls to the same tool apart without expanding either.
function summarize(input: unknown): string {
    if (!input || typeof input !== 'object') return '';
    const entries = Object.entries(input as Record<string, unknown>)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`);
    if (!entries.length) return '';
    const joined = entries.join(' ');
    return joined.length > 80 ? `${joined.slice(0, 79)}…` : joined;
}

export const ToolEvidence: React.FC<ToolEvidenceProps> = ({
    name, input, output, isError, running,
}) => {
    const [open, setOpen] = React.useState(false);
    const args = summarize(input);
    const body = renderInput(input);
    const result = (output ?? '').slice(0, MAX_OUTPUT_CHARS);
    const truncated = (output ?? '').length > MAX_OUTPUT_CHARS;

    return (
        <InlineAccordion
            className="mt-1"
            open={open}
            onToggle={() => setOpen((v) => !v)}
            header={
                <span className="d-inline-flex align-items-center gap-2">
                    {isError
                        ? <BsExclamationTriangleFill className="text-danger" />
                        : running ? <BsTools /> : <BsCheck2 />}
                    <span style={{ fontFamily: 'var(--type-code-family)' }}>{name}</span>
                    {args && (
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>{args}</span>
                    )}
                    {running && <span className="thinking-ellipsis" />}
                </span>
            }
            bodyStyle={{ whiteSpace: 'normal' }}
        >
            <div style={{ fontSize: '0.75rem' }}>
                {body && (
                    <>
                        <div className="text-muted mb-1">Request</div>
                        <pre style={preStyle}>{body}</pre>
                    </>
                )}
                {result && (
                    <>
                        <div className="text-muted mb-1 mt-2">
                            {isError ? 'Error' : 'Result'}
                        </div>
                        <pre style={preStyle}>
                            {result}
                            {truncated && '\n\n… [truncated]'}
                        </pre>
                    </>
                )}
                {!body && !result && (
                    <span className="text-muted">
                        {running ? 'running…' : 'no request or result recorded'}
                    </span>
                )}
            </div>
        </InlineAccordion>
    );
};

const preStyle: React.CSSProperties = {
    background: 'var(--color-surface-overlay)',
    border: '1px solid var(--color-border-subtle)',
    borderRadius: 'var(--radius-sm)',
    padding: '0.5rem',
    margin: 0,
    maxHeight: '18rem',
    overflow: 'auto',
    fontFamily: 'var(--type-code-family)',
    fontSize: 'var(--type-code-size)',
    color: 'var(--color-text-primary)',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
};

export default ToolEvidence;
